import { defineStore } from 'pinia'
import { ref } from 'vue'

export type ConfirmDeleteOptions = {
  title?: string
  message?: string
  name?: string
  confirmText?: string
  cancelText?: string
  confirmColor?: string
}

type ResolvedOptions = Required<Omit<ConfirmDeleteOptions, 'name'>> & { name: string }

const DEFAULTS: ResolvedOptions = {
  title: 'Delete',
  message: 'Are you sure you want to delete this item? This action cannot be undone.',
  name: '',
  confirmText: 'Delete',
  cancelText: 'Cancel',
  confirmColor: 'red darken-1'
}

export const useConfirmDeleteStore = defineStore('confirmDelete', () => {
  const isOpen = ref(false)
  const loading = ref(false)
  const options = ref<ResolvedOptions>({ ...DEFAULTS })

  let resolver: ((value: boolean) => void) | null = null

  const settle = (value: boolean) => {
    isOpen.value = false
    loading.value = false
    const resolve = resolver
    resolver = null
    resolve?.(value)
  }

  const confirm = (opts: ConfirmDeleteOptions = {}): Promise<boolean> => {
    if (resolver) settle(false)

    options.value = {
      title: opts.title ?? DEFAULTS.title,
      message: opts.message ?? DEFAULTS.message,
      name: opts.name ?? '',
      confirmText: opts.confirmText ?? DEFAULTS.confirmText,
      cancelText: opts.cancelText ?? DEFAULTS.cancelText,
      confirmColor: opts.confirmColor ?? DEFAULTS.confirmColor
    }
    isOpen.value = true
    return new Promise<boolean>((resolve) => {
      resolver = resolve
    })
  }

  const onConfirm = () => settle(true)
  const onCancel = () => settle(false)

  return { isOpen, loading, options, confirm, onConfirm, onCancel }
})
